import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { selectTourniquetIdentity, tourniquetIdentityActions } from '../../store/entities/tourniquetIdentity';
import { tourniquetTrainingsActions } from '../../store/entities/tourniquetTrainings';
import { getFirstLastNameObj, getLangNameObj, joinStrings, joinStrings2 } from '../../utils/format';
import { QcsButton } from '../common/basic/QcsButton';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { GridList } from '../common/grid/GridList';
import { TourniquetButtonContent } from './TourniquetButtonContent';
import { TourniquetCard, TourniquetContent, TourniquetHeader } from './TourniquetStyles';
import styled from '@emotion/styled';
import { visitationService } from '@api/services/services';
import { TourniquetTitle } from './TourniquetTitle';
import { InvitedPersonDto } from '@api/models/invitation/InvitedPersonDto';
import { VisitationEntryDto } from '@api/models/visitations/VisitationEntryDto';
import { InvitationDto } from '@api/models/invitation/InvitationDto';
import { resources } from '@api/resources';

const MobileHeader = styled.div(({ theme }) => ({
  display: 'block',

  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const DesktopHeader = styled(TourniquetHeader)(({ theme }) => ({
  display: 'none',
  gridTemplateColumns: '40px auto 40px',

  [theme.breakpoints.up('md')]: {
    display: 'grid',
  },
}));

interface GridItem {
  id: string;
  invitationName: string;
  workplaces: string;
  employeeDesktop: string;
  employeeMobile: string;
  responsiblePerson: string;
  invitation: InvitationDto;
}

export const TourniquetInvitationList: FC = () => {
  const { t, i18n } = useTranslation();
  const tourniquetIdentity = useAppSelector(selectTourniquetIdentity);
  const dispatch = useAppDispatch();

  const invitedPerson = useMemo<InvitedPersonDto | undefined>(
    () => tourniquetIdentity.invitations?.[0]?.persons?.find((p) => p.employeeId === tourniquetIdentity.employeeId),
    [tourniquetIdentity.invitations, tourniquetIdentity.employeeId],
  );

  const data = useMemo(() => {
    const personEmployeeId = tourniquetIdentity.employeeId;

    return (tourniquetIdentity.invitations ?? []).map<GridItem>((x) => {
      const person = x.persons?.find((p) => p.employeeId === personEmployeeId);

      return {
        id: x.id ?? '',
        invitationName: x.name ?? '',
        workplaces: joinStrings2(person?.workplaces?.map((w) => getLangNameObj(i18n, w))) ?? '',
        employeeDesktop: joinStrings(getFirstLastNameObj(x.employee), x.employee?.phone),
        employeeMobile: getFirstLastNameObj(x.employee),
        responsiblePerson: getFirstLastNameObj(x.responsiblePerson),
        invitation: x,
      };
    });
  }, [i18n, tourniquetIdentity.invitations, tourniquetIdentity.employeeId]);

  const handleRenderData = (item: GridItem) => {
    const handleConfirm = async () => {
      const data: VisitationEntryDto = {
        invitation: item.id,
        pin: tourniquetIdentity.pin ?? '',
      };

      const incompleteTrainingsRes = await visitationService.incompleteTrainings(data);

      const validateEntry = await visitationService.accessPointValidateEntry(data);

      dispatch(tourniquetTrainingsActions.success(incompleteTrainingsRes.data));

      dispatch(
        tourniquetIdentityActions.success({
          ...tourniquetIdentity,
          invitation: item.invitation,
          validationErrors: validateEntry.data,
        }),
      );
    };

    return (
      <>
        <QcsTableCell>{item.invitationName}</QcsTableCell>
        <QcsTableCell>{item.workplaces}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.employeeDesktop}</QcsTableCell>
        <QcsTableCell showOnlyOnMobile={true}>{item.employeeMobile}</QcsTableCell>
        <QcsTableCell hideOnMobile={true}>{item.responsiblePerson}</QcsTableCell>
        <QcsTableCell align="right">
          <QcsLoadingButton
            variant="contained"
            resourceLoading={[resources.incompleteTrainings, resources.accessPointValidateEntry]}
            onClick={handleConfirm}
          >
            {t('common.confirm')}
          </QcsLoadingButton>
        </QcsTableCell>
      </>
    );
  };

  const handleAbort = () => {
    dispatch(tourniquetIdentityActions.default());
  };

  const invitedPersonFullName = () => {
    return invitedPerson ? `${invitedPerson.name} ${invitedPerson.lastName}` : '';
  };

  return (
    <TourniquetButtonContent backOnClickMobile={handleAbort}>
      <TourniquetCard>
        <TourniquetContent>
          <MobileHeader>
            <TourniquetTitle style={{ marginBottom: '1rem' }} />
          </MobileHeader>
          <DesktopHeader>
            <QcsButton sx={{ width: '80px' }} variant="outlined" color="error" onClick={handleAbort}>
              {t('common.back')}
            </QcsButton>
            <TourniquetTitle />
          </DesktopHeader>
          <div style={{ padding: '0.5rem' }}>{invitedPersonFullName() + ' ' + tourniquetIdentity?.invitations?.[0]?.supplier?.name}</div>
          <GridList<GridItem>
            headers={[
              { captionStr: 'tourniquet.invitationName' },
              { captionStr: 'tourniquet.workplaces' },
              { captionStr: 'tourniquet.employee' },
              {
                captionStr: 'tourniquet.responsiblePerson',
                hideOnMobile: true,
              },
              {},
            ]}
            data={data}
            renderData={handleRenderData}
            search={false}
            hidePagination={true}
          />
        </TourniquetContent>
      </TourniquetCard>
    </TourniquetButtonContent>
  );
};
